/* eslint-disable simple-import-sort/imports */
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import React, { useMemo } from 'react';

import { PackagingPicker } from 'components/packaging/components/packagingPOV2/formComponents/PackagingPicker';
import { Alert, AlertDescription, AlertTitle } from '~/shadcn/components/ui/Alert';
import { Button } from '~/shadcn/components/ui/Button';
import { Label } from '~/shadcn/components/ui/Label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/shadcn/components/ui/Table';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/shadcn/components/ui/tooltip';

import {
    FALLBACK_TEXT,
    formatNumber,
    normalizeOptionalText,
} from '~/components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDisplayUtils';
import {
    computePackagingItemTotalCost,
    DUPLICATE_PACKAGING_COMPANY_SOURCE_MESSAGE,
    formatPackagingCurrency,
} from '~/components/packaging/components/packagingPOV2/utils/packagingPOValidation';

import { CompanySourceLabel, CompanySourcePicker } from 'shared/components/companySource';

import { PackagingPurchaseOrderItemEditTable } from './PackagingPurchaseOrderItemEditTable';

const EMPTY_SET = new Set();
const noop = () => {};
const labelClassName =
    'text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground leading-tight min-h-[28px]';

/**
 * Normalizes API item shape to table row shape with stable id and estimatedFreightCost.
 * @param {Array<Object>} items - Raw items from packaging PO detail API.
 * @returns {Array<Object>} Normalized rows.
 */
function normalizePackagingItems(items) {
    if (!Array.isArray(items)) return [];

    return items.map((item) => {
        const id = item?.poMapID ?? item?.poMapId ?? item?.id ?? null;
        return {
            ...item,
            id,
            packagingModelId: item?.packagingModelId ?? item?.packagingId ?? null,
            masterSku: item?.masterSku ?? item?.sku ?? null,
            description: item?.description ?? item?.itemName ?? null,
            unitsPerCase: item?.unitsPerCase ?? null,
            numPallets: item?.numPallets ?? null,
            qty: item?.qty ?? null,
            unitPrice: item?.unitPrice ?? null,
            vendorSku: item?.vendorSku ?? null,
            notes: item?.notes ?? '',
        };
    });
}

function DescriptionCell({ getValue }) {
    const value = normalizeOptionalText(getValue());
    if (value === FALLBACK_TEXT) return <span className="text-left">{value}</span>;

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <span className="block max-w-[300px] truncate text-left">{value}</span>
            </TooltipTrigger>
            <TooltipContent side="top">{value}</TooltipContent>
        </Tooltip>
    );
}

function CompanySourceTableCell({ row }) {
    return (
        <CompanySourceLabel
            companySourceId={row.original?.companySourceId}
            companySource={row.original?.companySource}
            showDisplayName={false}
        />
    );
}

/**
 * Renders the packaging purchase order items card shell.
 * @param {JSX.Element} children - The children to render inside the card shell.
 * @returns {JSX.Element} Card shell.
 */
function PackagingPurchaseOrderItemsCardShell({ children }) {
    return (
        <div className="rounded-md border bg-card p-6 shadow-sm">
            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                    <h2 className="text-left text-lg font-semibold tracking-tight text-foreground">
                        Packaging Purchase Order Items
                    </h2>
                </div>
                {children}
            </div>
        </div>
    );
}

/**
 * Displays the packaging purchase order items section in either read-only or inline-edit mode.
 *
 * @param {Object} props
 * @param {Object|null} [props.purchaseOrder] - Purchase order detail object (must include `items`).
 * @param {boolean} [props.isEditing=false] - When true, shows inline item edit UI.
 * @param {Object[]} [props.addedItems=[]] - Newly added items (edit mode).
 * @param {Set<string|number>} [props.removedItemIds] - Set of PO map IDs marked for removal.
 * @param {(updater: Set|Function) => void} [props.setRemovedItemIds] - Setter for removedItemIds.
 * @param {{current: {inputs: Object, selects: Object}}} [props.itemEditStateRef] - Ref for uncontrolled edit values.
 * @param {() => void} [props.onItemEditsChange] - Callback when item edits change.
 * @param {(rowId: string) => void} [props.onRemoveNewRow] - Callback to remove a newly added row.
 * @param {boolean} [props.packagingOptionsEnabled=true] - When true, packaging picker query runs (e.g. when editing).
 * @param {{ value: string, label: string, model: Object }|null} [props.selectedPackagingOption=null] - Selected packaging option for add item.
 * @param {(option: { value, label, model }|null) => void} [props.onSelectedPackagingChange] - Setter for selected packaging option.
 * @param {() => void} [props.onAddItem] - Add item handler.
 * @param {boolean} [props.canAddItem=false] - Whether Add Item button is enabled (e.g. when a packaging SKU is selected).
 * @param {number[]} [props.effectiveCompanySourceIds=[]] - Selected partner ids for add-item strip.
 * @param {(value: number[]) => void} [props.onCompanySourceIdsChange] - Setter for selected partner ids.
 * @param {number[]|undefined} [props.eligibleSourceIds] - Eligible partners for selected packaging SKU.
 * @param {boolean} [props.hasInvalidCompanySourceEligibility=false] - True when packaging SKU has empty companySources.
 * @param {boolean} [props.canSelectCompanySources=false] - Whether partner picker is enabled.
 * @param {string[]} [props.itemErrors=[]] - Item-level validation messages (edit mode).
 * @param {string} [props.duplicateLineItemError=''] - Live duplicate line-item feedback while editing.
 * @param {boolean} [props.showValidation=false] - Whether to show validation state (e.g. after attempted save).
 * @returns {JSX.Element} Packaging PO items card.
 */
export function PackagingPurchaseOrderItemsCard({
    addedItems = [],
    canAddItem = false,
    canSelectCompanySources = false,
    duplicateLineItemError = '',
    effectiveCompanySourceIds = [],
    eligibleSourceIds,
    hasInvalidCompanySourceEligibility = false,
    isEditing = false,
    itemEditStateRef,
    itemErrors = [],
    onAddItem = noop,
    onCompanySourceIdsChange = noop,
    onItemEditsChange = noop,
    onRemoveNewRow = noop,
    onSelectedPackagingChange = noop,
    packagingOptionsEnabled = true,
    purchaseOrder = null,
    removedItemIds = EMPTY_SET,
    selectedPackagingOption = null,
    setRemovedItemIds = noop,
    showValidation = false,
}) {
    const rawItems = purchaseOrder?.items ?? [];
    const tableData = useMemo(() => normalizePackagingItems(rawItems), [rawItems]);
    const combinedTableData = useMemo(() => [...tableData, ...(addedItems ?? [])], [addedItems, tableData]);

    const columnDefs = useMemo(() => {
        const columns = [
            {
                id: 'masterSku',
                header: 'Item Master SKU',
                accessorFn: (row) => row?.masterSku ?? row?.sku ?? null,
                cell: ({ getValue }) => normalizeOptionalText(getValue()),
            },
            {
                id: 'description',
                header: 'Description',
                accessorFn: (row) => row?.description ?? row?.itemName ?? null,
                cell: DescriptionCell,
            },
            {
                id: 'companySourceId',
                header: 'Company Source',
                accessorFn: (row) => row?.companySourceId ?? row?.companySource ?? null,
                cell: CompanySourceTableCell,
            },
            {
                id: 'qty',
                header: 'Quantity (cases)',
                accessorFn: (row) => row?.qty ?? null,
                cell: ({ getValue }) => formatNumber(getValue()),
            },
            {
                id: 'unitPrice',
                header: 'Unit Price',
                accessorFn: (row) => row?.unitPrice ?? null,
                cell: ({ getValue }) => formatPackagingCurrency(getValue()),
            },
            {
                id: 'numPallets',
                header: 'Pallets per SKU',
                accessorFn: (row) => row?.numPallets ?? null,
                cell: ({ getValue }) => formatNumber(getValue()),
            },
            {
                id: 'unitsPerCase',
                header: 'Units per Case',
                accessorFn: (row) => row?.unitsPerCase ?? null,
                cell: ({ getValue }) => formatNumber(getValue()),
            },
            {
                id: 'vendorSku',
                header: 'Vendor SKU',
                accessorFn: (row) => row?.vendorSku ?? null,
                cell: ({ getValue }) => normalizeOptionalText(getValue()),
            },
            {
                id: 'notes',
                header: 'Notes',
                accessorFn: (row) => row?.notes ?? null,
                cell: ({ getValue }) => normalizeOptionalText(getValue()),
            },
            {
                id: 'totalUnits',
                header: 'Total Units',
                accessorFn: (row) => {
                    const qtyRaw = row?.qty;
                    const unitsPerCaseRaw = row?.unitsPerCase;
                    if (qtyRaw == null || unitsPerCaseRaw == null) return null;
                    const qty = Number(qtyRaw);
                    const unitsPerCase = Number(unitsPerCaseRaw);
                    return Number.isFinite(qty) && Number.isFinite(unitsPerCase) ? qty * unitsPerCase : null;
                },
                cell: ({ getValue }) => {
                    const total = getValue();
                    return total != null ? formatNumber(total) : '';
                },
            },
            {
                id: 'totalCost',
                header: 'Total Cost',
                accessorFn: (row) => computePackagingItemTotalCost(row),
                cell: ({ getValue }) => {
                    const total = getValue();
                    return total != null ? formatPackagingCurrency(total) : '';
                },
            },
        ];
        return columns;
    }, []);

    const table = useReactTable({
        data: tableData,
        columns: columnDefs,
        getCoreRowModel: getCoreRowModel(),
    });

    const itemCount = isEditing ? combinedTableData.length : tableData.length;
    const saveValidationItemErrors = showValidation
        ? itemErrors.filter((message) => message !== DUPLICATE_PACKAGING_COMPANY_SOURCE_MESSAGE)
        : [];
    const hasItemFeedback = Boolean(duplicateLineItemError) || saveValidationItemErrors.length > 0;

    if (isEditing) {
        return (
            <PackagingPurchaseOrderItemsCardShell>
                <div className="flex flex-col gap-4">
                    {hasItemFeedback ? (
                        <Alert variant="destructive">
                            <AlertTitle className="text-left">Fix the following item issues to save</AlertTitle>
                            <AlertDescription className="text-left">
                                <ul className="list-disc pl-5 text-left">
                                    {duplicateLineItemError ? <li>{duplicateLineItemError}</li> : null}
                                    {saveValidationItemErrors.map((message) => (
                                        <li key={message}>{message}</li>
                                    ))}
                                </ul>
                            </AlertDescription>
                        </Alert>
                    ) : null}
                    <div className="flex flex-wrap items-end gap-4 border-b border-border pb-4">
                        <Label className={`${labelClassName} sr-only`}>Add Item to Purchase Order</Label>
                        <div className="flex flex-col gap-2 min-w-[280px]">
                            <PackagingPicker
                                id="packaging-item-combobox"
                                enabled={packagingOptionsEnabled}
                                label={
                                    <>
                                        Packaging SKU <span className="text-destructive">*</span>
                                    </>
                                }
                                value={selectedPackagingOption?.value ?? ''}
                                onValueChange={onSelectedPackagingChange}
                                placeholder="Select packaging SKU"
                            />
                        </div>
                        <div className="flex flex-col gap-2 min-w-[200px]">
                            <CompanySourcePicker
                                mode="multiple"
                                value={effectiveCompanySourceIds}
                                onValueChange={onCompanySourceIdsChange}
                                eligibleSourceIds={canSelectCompanySources ? eligibleSourceIds : undefined}
                                disabled={!canSelectCompanySources || hasInvalidCompanySourceEligibility}
                            />
                            {hasInvalidCompanySourceEligibility ? (
                                <p className="text-sm text-destructive">
                                    This packaging SKU has no eligible company sources and cannot be added to a purchase
                                    order.
                                </p>
                            ) : null}
                        </div>
                        <Button type="button" size="sm" onClick={onAddItem} disabled={!canAddItem}>
                            Add Item
                        </Button>
                    </div>

                    {combinedTableData.length > 0 ? (
                        <PackagingPurchaseOrderItemEditTable
                            rows={combinedTableData}
                            itemEditStateRef={itemEditStateRef}
                            removedItemIds={removedItemIds}
                            setRemovedItemIds={setRemovedItemIds}
                            onItemEditsChange={onItemEditsChange}
                            onRemoveNewRow={onRemoveNewRow}
                        />
                    ) : (
                        <div className="mt-6">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Item Master SKU</TableHead>
                                        <TableHead>Description</TableHead>
                                        <TableHead>Company Source</TableHead>
                                        <TableHead>Quantity (cases)</TableHead>
                                        <TableHead>Unit Price</TableHead>
                                        <TableHead>Pallets per SKU</TableHead>
                                        <TableHead>Units per Case</TableHead>
                                        <TableHead>Vendor SKU</TableHead>
                                        <TableHead>Notes</TableHead>
                                        <TableHead>Total Units</TableHead>
                                        <TableHead>Total Cost</TableHead>
                                        <TableHead>Remove</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    <TableRow>
                                        <TableCell colSpan={12} className="text-sm text-muted-foreground">
                                            No items added yet.
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </div>
            </PackagingPurchaseOrderItemsCardShell>
        );
    }

    if (itemCount > 0) {
        return (
            <PackagingPurchaseOrderItemsCardShell>
                <Table
                    className="rounded-md border border-border"
                    style={{
                        minWidth: '1000px',
                    }}
                >
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead
                                        key={header.id}
                                        className="whitespace-nowrap px-2 py-2 text-left align-top text-xs font-semibold uppercase tracking-wide"
                                    >
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows.map((row) => (
                            <TableRow key={row.id}>
                                {row.getVisibleCells().map((cell) => (
                                    <TableCell
                                        key={cell.id}
                                        className="whitespace-pre-wrap break-words px-2 py-2 text-left align-top text-sm"
                                    >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </PackagingPurchaseOrderItemsCardShell>
        );
    }

    return (
        <PackagingPurchaseOrderItemsCardShell>
            <div className="flex min-h-[160px] items-center justify-center rounded-md border border-border px-4 py-10 text-center text-sm text-muted-foreground">
                No items associated with this purchase order.
            </div>
        </PackagingPurchaseOrderItemsCardShell>
    );
}

PackagingPurchaseOrderItemsCard.displayName = 'PackagingPurchaseOrderItemsCard';
